// frontend/src/pages/purchase/PurchaseTransaction.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Package, Warehouse, Plus, Building, Calendar, Hash, DollarSign,
  ClipboardList, Download, AlertCircle, Save, X, RefreshCw,
  ArrowLeftRight, AlertTriangle, ArrowUp, ArrowDown,
  Loader2, Truck, CreditCard, FileText, Percent, Trash2, Calculator,
  Wallet, CreditCard as CreditCardIcon, Smartphone, Layers, Box,
  History, Eye, Receipt, Users, Banknote, CircleDollarSign
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./PurchaseTransaction.css";

const url = process.env.REACT_APP_BACKEND_URL;

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");

const getRecordId = (value) => value?._id || value?.id || value || "";

const getTaxValue = (tax) =>
  Number(
    tax?.value ??
    tax?.tax_rate ??
    tax?.rate ??
    tax?.percentage ??
    tax?.amount ??
    0,
  );

const getDiscountValue = (discount) =>
  Number(
    discount?.value ??
    discount?.discount_value ??
    discount?.rate ??
    discount?.percentage ??
    discount?.amount ??
    0,
  );

const isPurchaseReturnType = (type) => {
  const name = normalizeText(type?.transaction_type);
  return (
    name === "purchase return" ||
    name.includes("purchase return") ||
    (name.includes("return") && String(type?.sign || "") === "-")
  );
};

const isPurchaseType = (type) => {
  const name = normalizeText(type?.transaction_type);
  return (
    !isPurchaseReturnType(type) &&
    (name === "purchase" ||
      (name.includes("purchase") && String(type?.sign || "") === "+"))
  );
};

const getModeTransactionType = (types, returnMode) =>
  types.find((type) =>
    returnMode ? isPurchaseReturnType(type) : isPurchaseType(type),
  );

const PurchaseTransaction = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});
  const [paymentMethodsData, setPaymentMethodsData] = useState({
    methods: [],
  });
  const [discounts, setDiscounts] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [totalPayments, setTotalPayments] = useState(0);

  const [isReturnMode, setIsReturnMode] = useState(false);
  const isReturnModeRef = useRef(false);
  const [returnableTransactions, setReturnableTransactions] = useState([]);
  const [selectedOriginalTransaction, setSelectedOriginalTransaction] = useState(null);
  const [loadingReturnTransactions, setLoadingReturnTransactions] = useState(false);

  const getPaymentStatusFromTotals = (grandTotal, totalPaid) => {
    const balance = Math.abs(grandTotal) - totalPaid;
    if (balance === 0) return "paid";
    if (balance > 0 && totalPaid > 0) return "partial";
    if (balance > 0 && totalPaid === 0) return "notpaid";
    return "pending";
  };

  const paymentMethodOptions = [
    { value: "cash", label: "Cash", icon: <CreditCardIcon size={16} /> },
    { value: "bank", label: "Bank", icon: <Banknote size={16} /> },
    { value: "mobile_money", label: "Mobile Money", icon: <Smartphone size={16} /> },
    { value: "credit", label: "Credit", icon: <CircleDollarSign size={16} /> }
  ];

  const getAccountsForMethod = (methodKey) =>
    paymentMethodsData.methods.find(
      (method) => method.key === methodKey,
    )?.accounts || [];

  const [payments, setPayments] = useState([
    {
      method: "credit",
      account_id: "",
      account_name: "",
      amount: "",
      reference: "",
    },
  ]);

  const [paymentInfo, setPaymentInfo] = useState({
    message: "",
    type: "info",
  });

  const [formData, setFormData] = useState({
    warehouse_id: "",
    supplier_id: "",
    transaction_type_id: "",
    invoice_no: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    payment_status: "pending",
    notes: "",
    shipping_cost: "0",
    other_charges: "0",
    return_reason: "",
    purchase_invoice_id: "",
  });

  const [productsData, setProductsData] = useState([
    {
      product_id: "",
      quantity: "",
      unit_cost: "",
      discount_id: "",
      tax_id: "",
      discount_percent: "0",
      discount_flat: "0",
      manual_discount: "0",
      total_cost: "0",
      batch_number: "",
      expiry_date: "",
    }
  ]);

  const [purchaseAdjustments, setPurchaseAdjustments] = useState({
    discount_id: "",
    tax_id: "",
    manual_discount: "",
  });

  const [errors, setErrors] = useState({});
  const [focusedFields, setFocusedFields] = useState({});

  const [totals, setTotals] = useState({
    subTotal: 0,
    totalDiscount: 0,
    totalTax: 0,
    shippingCost: 0,
    otherCharges: 0,
    grandTotal: 0,
    totalAmountPaid: 0,
    netAmount: 0,
    paymentStatus: "pending",
  });

  useEffect(() => {
    isReturnModeRef.current = isReturnMode;
  }, [isReturnMode]);

  useEffect(() => {
    if (transactionTypes.length === 0) return;

    const modeType = getModeTransactionType(
      transactionTypes,
      isReturnMode,
    );

    if (!modeType) {
      setFormData((previous) => ({
        ...previous,
        transaction_type_id: "",
      }));
      return;
    }

    const modeTypeId = String(getRecordId(modeType));
    setFormData((previous) =>
      String(previous.transaction_type_id || "") === modeTypeId
        ? previous
        : { ...previous, transaction_type_id: modeTypeId },
    );
  }, [isReturnMode, transactionTypes]);


  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin' || currentUser?.role === 'purchase' || currentUser?.role === 'user';
  const canCreate = isCompanyAdmin || isSystemAdmin;

  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  const fetchPaymentMethods = async () => {
    try {
      const companyId = getCompanyId();

      if (!companyId) return;

      const response = await axios.get(
        `${url}/api/purchaseorders/payment-methods`,
        {
          params: { company_id: companyId },
        },
      );

      const options = response.data?.data || {};
      setPaymentMethodsData({
        methods: Array.isArray(options.methods) ? options.methods : [],
      });
      setDiscounts(
        Array.isArray(options.discounts) ? options.discounts : [],
      );
      setTaxes(Array.isArray(options.taxes) ? options.taxes : []);
      setPayments([
        {
          method: "credit",
          account_id: "",
          account_name: "",
          amount: "",
          reference: "",
        },
      ]);
    } catch (error) {
      setPaymentMethodsData({ methods: [] });
      setDiscounts([]);
      setTaxes([]);
      setPayments([
        {
          method: "credit",
          account_id: "",
          account_name: "",
          amount: "",
          reference: "",
        },
      ]);
      toast.error(
        error.response?.data?.message ||
        "Failed to load purchase payment methods",
      );
    }
  };

  const fetchPaymentHistory = async (invoiceId) => {
    if (!invoiceId) return;

    try {
      setLoadingPayments(true);

      const response = await axios.get(`${url}/api/supplierpayment/by-invoice/${invoiceId}`);

      if (response.data.success) {
        const paymentsData = response.data.data || [];
        setPaymentHistory(paymentsData);

        const total = paymentsData.reduce((sum, p) => sum + (p.total_paying_now || 0), 0);
        setTotalPayments(total);
      }
    } catch (error) {
    } finally {
      setLoadingPayments(false);
    }
  };

  // ✅ FIX: Merge stock data with ALL products (includes 0-stock items)
  const fetchProductsForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setFilteredProducts(products);
      setProductStock({});
      return;
    }

    try {
      const companyId = getCompanyId();

      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: {
          company_id: companyId,
          status: 'active'
        }
      });

      if (response.data.success) {
        const stockMap = {};

        // 1. Build the stock map from the API response
        for (const stock of response.data.data || []) {
          let productId, avgCost = 0;

          if (stock.product_id && typeof stock.product_id === "object") {
            productId = stock.product_id._id;
            avgCost = stock.avg_cost || stock.product_id.purchase_cost || stock.product_id.cost_price || stock.product_id.purchase_price || stock.product_id.unit_price || 0;
          } else {
            productId = stock.product_id;
            const foundProduct = products.find(p => p._id === productId);
            avgCost = stock.avg_cost || foundProduct?.purchase_cost || foundProduct?.cost_price || foundProduct?.purchase_price || foundProduct?.unit_price || 0;
          }

          stockMap[String(productId)] = {
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            reserved_stock: stock.reserved_qty || 0,
            avg_cost: avgCost,
          };
        }

        // 2. Merge stock data with ALL products (includes services or 0-stock items)
        const mergedProducts = products.map(p => {
          const stockInfo = stockMap[String(p._id)];
          return {
            _id: p._id,
            product_code: p.product_code,
            product_name: p.product_name,
            // Default to 0 if no stock record exists
            available_stock: stockInfo ? stockInfo.available_stock : 0,
            avg_cost: stockInfo ? stockInfo.avg_cost : (p.purchase_cost || p.cost_price || p.purchase_price || p.unit_price || 0),
          };
        });

        setProductStock(stockMap);
        setFilteredProducts(mergedProducts);
      } else {
        setFilteredProducts(products);
        setProductStock({});
      }
    } catch (error) {
      setFilteredProducts(products);
      setProductStock({});
    }
  };

  useEffect(() => {
    if (warehouses.length > 0 && formData.warehouse_id && products.length > 0) {
      fetchProductsForWarehouse(formData.warehouse_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouses, formData.warehouse_id, products]);

  const handleWarehouseSelect = (selected) => {
    const warehouseId = selected ? selected.value : "";
    setFormData((prev) => ({ ...prev, warehouse_id: warehouseId }));
    if (warehouseId) {
      fetchProductsForWarehouse(warehouseId);
    } else {
      setFilteredProducts(products);
      setProductStock({});
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!canCreate) {
        toast.error("You don't have permission");
        navigate("/");
        return;
      }

      try {
        setLoading(true);

        const companyId = getCompanyId();

        if (!companyId) {
          setError("No company ID found");
          setLoading(false);
          return;
        }

        await fetchPaymentMethods();

        const params = { company_id: companyId, status: 'active' };

        try {
          const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
          if (whRes.data?.success) {
            setWarehouses(whRes.data.data || []);
          }
        } catch (err) {
          toast.error("Failed to load warehouses");
        }

        try {
          const supRes = await axios.get(`${url}/api/supplier/dropdown`, { params });
          if (supRes.data?.success) {
            setSuppliers(supRes.data.data || []);
          }
        } catch (err) {
          toast.error("Failed to load suppliers");
        }

        try {
          const ttRes = await axios.get(`${url}/api/transactiontype/dropdown`, { params });
          if (ttRes.data?.success) {
            const types = ttRes.data.data || [];
            const purchaseTypes = types.filter(
              (type) => isPurchaseType(type) || isPurchaseReturnType(type),
            );
            setTransactionTypes(purchaseTypes);

            const purchaseType = getModeTransactionType(purchaseTypes, false);
            if (purchaseType) {
              setFormData((previous) => ({
                ...previous,
                transaction_type_id: getRecordId(purchaseType),
              }));
            }
          }
        } catch (err) {
          toast.error("Failed to load transaction types");
        }

        try {
          const prodRes = await axios.get(`${url}/api/products/dropdown`, { params });
          if (prodRes.data?.success) {
            setProducts(prodRes.data.data || []);
            setFilteredProducts(prodRes.data.data || []);
          }
        } catch (err) {
          toast.error("Failed to load products");
        }

        if (formData.warehouse_id) {
          await fetchProductsForWarehouse(formData.warehouse_id);
        }

        const urlParams = new URLSearchParams(window.location.search);
        const invoiceId = urlParams.get('invoiceId');
        if (invoiceId) {
          setFormData(prev => ({ ...prev, purchase_invoice_id: invoiceId }));
          await fetchPaymentHistory(invoiceId);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roundMoney = (value) =>
    Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

  const allocateDocumentAmount = (amount, lineIndex, sourceProducts = productsData) => {
    const grossValues = sourceProducts.map(
      (row) => (Number(row.quantity) || 0) * (Number(row.unit_cost) || 0),
    );
    const totalGross = grossValues.reduce((sum, value) => sum + value, 0);
    const safeAmount = Math.max(0, Number(amount) || 0);

    if (safeAmount <= 0 || totalGross <= 0 || lineIndex < 0) return 0;

    if (lineIndex === grossValues.length - 1) {
      const allocatedBefore = grossValues
        .slice(0, lineIndex)
        .reduce(
          (sum, gross) =>
            sum + roundMoney((safeAmount * gross) / totalGross),
          0,
        );
      return Math.max(0, roundMoney(safeAmount - allocatedBefore));
    }

    return roundMoney((safeAmount * grossValues[lineIndex]) / totalGross);
  };

  const calculateProductLine = (
    product,
    suppliedIndex = -1,
    sourceProducts = productsData,
  ) => {
    const quantity = Number(product.quantity) || 0;
    const unitCost = Number(product.unit_cost) || 0;
    const gross = roundMoney(quantity * unitCost);
    const lineIndex =
      suppliedIndex >= 0 ? suppliedIndex : sourceProducts.indexOf(product);

    // In return mode, reverse the exact discount, tax, shipping, and other
    // charges saved on the original purchase line.
    if (isReturnMode && selectedOriginalTransaction) {
      const originalQuantity = Math.abs(
        Number(selectedOriginalTransaction.quantity || 0),
      );
      const returnRatio =
        originalQuantity > 0 ? Math.min(quantity / originalQuantity, 1) : 0;

      const originalDiscountAmount = Math.abs(
        Number(
          selectedOriginalTransaction.discount_amount ??
          selectedOriginalTransaction.total_discount ??
          0,
        ),
      );
      const originalTaxAmount = Math.abs(
        Number(
          selectedOriginalTransaction.tax_amount ??
          selectedOriginalTransaction.total_tax ??
          0,
        ),
      );

      // NEW: pull the original line's shipping + other charges so they
      // are reversed proportionally with the returned quantity.
      const originalShippingCost = Math.abs(
        Number(selectedOriginalTransaction.shipping_cost ?? 0),
      );
      const originalOtherCharges = Math.abs(
        Number(selectedOriginalTransaction.other_charges ?? 0),
      );

      const discountAmount = roundMoney(
        originalDiscountAmount * returnRatio,
      );
      const taxAmount = roundMoney(originalTaxAmount * returnRatio);
      const shippingAmount = roundMoney(
        originalShippingCost * returnRatio,
      );
      const otherAmount = roundMoney(
        originalOtherCharges * returnRatio,
      );

      const originalTaxId = getRecordId(
        selectedOriginalTransaction.tax_id ||
        selectedOriginalTransaction.tax,
      );
      const originalTax = taxes.find(
        (row) => String(getRecordId(row)) === String(originalTaxId),
      );
      const isInclusiveTax = Boolean(
        originalTax?.price_inclusive ??
        selectedOriginalTransaction.tax?.price_inclusive ??
        selectedOriginalTransaction.price_inclusive ??
        false,
      );
      const exclusiveTax = isInclusiveTax ? 0 : taxAmount;

      return {
        gross,
        discountAmount,
        taxAmount,
        exclusiveTax,
        shippingAmount,
        otherAmount,
        total: roundMoney(
          Math.max(0, gross - discountAmount) +
          exclusiveTax +
          shippingAmount +
          otherAmount,
        ),
        discountPercent: Number(
          selectedOriginalTransaction.discount_percent || 0,
        ),
        discountFlat: Number(
          selectedOriginalTransaction.discount_flat || 0,
        ),
      };
    }

    const discount = discounts.find(
      (row) => String(row._id) === String(purchaseAdjustments.discount_id),
    );
    const tax = taxes.find(
      (row) => String(row._id) === String(purchaseAdjustments.tax_id),
    );

    let selectedDiscountAmount = 0;
    if (discount?.discount_type === "percentage") {
      selectedDiscountAmount = roundMoney(
        (gross * Number(discount.value || 0)) / 100,
      );
    } else if (discount?.discount_type === "fixed") {
      selectedDiscountAmount = allocateDocumentAmount(
        discount.value,
        lineIndex,
        sourceProducts,
      );
    }

    const manualDiscount = allocateDocumentAmount(
      purchaseAdjustments.manual_discount,
      lineIndex,
      sourceProducts,
    );
    const discountAmount = roundMoney(
      Math.min(gross, selectedDiscountAmount + manualDiscount),
    );
    const afterDiscount = roundMoney(Math.max(0, gross - discountAmount));

    let selectedTaxAmount = 0;
    let exclusiveTax = 0;
    if (tax?.tax_type === "percentage") {
      const rate = Number(tax.value || 0);
      selectedTaxAmount = tax.price_inclusive
        ? roundMoney(afterDiscount - afterDiscount / (1 + rate / 100))
        : roundMoney((afterDiscount * rate) / 100);
      exclusiveTax = tax.price_inclusive ? 0 : selectedTaxAmount;
    } else if (tax?.tax_type === "fixed") {
      selectedTaxAmount = allocateDocumentAmount(
        tax.value,
        lineIndex,
        sourceProducts,
      );
      exclusiveTax = selectedTaxAmount;
    }

    const taxAmount = roundMoney(selectedTaxAmount);
    exclusiveTax = roundMoney(exclusiveTax);

    return {
      gross,
      discountAmount,
      taxAmount,
      exclusiveTax,
      shippingAmount: 0,
      otherAmount: 0,
      total: roundMoney(afterDiscount + exclusiveTax),
      discountPercent:
        discount?.discount_type === "percentage"
          ? Number(discount.value || 0)
          : 0,
      discountFlat: 0,
    };
  };

  useEffect(() => {
    if (isReturnMode) return;

    const selectedDiscount = discounts.find(
      (row) => String(row._id) === String(purchaseAdjustments.discount_id),
    );
    const selectedTax = taxes.find(
      (row) => String(row._id) === String(purchaseAdjustments.tax_id),
    );

    const grossValues = productsData.map(
      (row) => (Number(row.quantity) || 0) * (Number(row.unit_cost) || 0),
    );
    const totalGross = grossValues.reduce((sum, value) => sum + value, 0);
    const manualDiscountTotal = Math.max(0, Number(purchaseAdjustments.manual_discount) || 0);

    const allocate = (amount, gross, index) => {
      if (amount <= 0 || totalGross <= 0) return 0;
      if (index === grossValues.length - 1) {
        const prior = grossValues.slice(0, index).reduce(
          (sum, value) => sum + Math.round((amount * value / totalGross) * 100) / 100,
          0,
        );
        return Math.max(0, Math.round((amount - prior) * 100) / 100);
      }
      return Math.round((amount * gross / totalGross) * 100) / 100;
    };

    setProductsData((current) => {
      let changed = false;
      const next = current.map((row, index) => {
        const gross = grossValues[index] || 0;
        const fixedDiscountTotal = selectedDiscount?.discount_type === "fixed"
          ? Math.max(0, Number(selectedDiscount.value) || 0)
          : 0;
        const fixedTaxTotal = selectedTax?.tax_type === "fixed"
          ? Math.max(0, Number(selectedTax.value) || 0)
          : 0;

        const updated = {
          ...row,
          discount_id: purchaseAdjustments.discount_id || "",
          tax_id: purchaseAdjustments.tax_id || "",
          manual_discount: String(allocate(manualDiscountTotal, gross, index)),
          _selected_discount_alloc: selectedDiscount?.discount_type === "fixed"
            ? allocate(fixedDiscountTotal, gross, index)
            : undefined,
          _selected_tax_alloc: selectedTax?.tax_type === "fixed"
            ? allocate(fixedTaxTotal, gross, index)
            : undefined,
        };

        if (JSON.stringify(updated) !== JSON.stringify(row)) changed = true;
        return updated;
      });
      return changed ? next : current;
    });
  }, [
    purchaseAdjustments,
    discounts,
    taxes,
    isReturnMode,
    productsData.map((row) => `${row.quantity}:${row.unit_cost}`).join("|"),
  ]);

  // Calculate totals. In return mode, accumulate per-line shipping and
  // other charges so the return reverses the original purchase correctly.
  useEffect(() => {
    let subTotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let exclusiveTax = 0;
    let returnShipping = 0;
    let returnOther = 0;

    productsData.forEach((product, index) => {
      const calculated = calculateProductLine(product, index, productsData);
      subTotal += calculated.gross;
      totalDiscount += calculated.discountAmount;
      totalTax += calculated.taxAmount;
      exclusiveTax += calculated.exclusiveTax;
      if (isReturnMode) {
        returnShipping += Number(calculated.shippingAmount || 0);
        returnOther += Number(calculated.otherAmount || 0);
      }
    });

    // In return mode the shipping and other charges are already included
    // in the per-line calculations, so we use the accumulated values
    // instead of the raw form fields.
    const shipping = isReturnMode
      ? returnShipping
      : Number(formData.shipping_cost) || 0;

    const other = isReturnMode
      ? returnOther
      : Number(formData.other_charges) || 0;

    const grandTotal =
      subTotal - totalDiscount + exclusiveTax + shipping + other;

    const paid = payments.reduce(
      (sum, payment) =>
        sum +
        (payment.method === "credit"
          ? 0
          : Number(payment.amount) || 0),
      0,
    );

    const displayGrandTotal = isReturnMode
      ? -Math.abs(grandTotal)
      : Math.abs(grandTotal);

    const payableTotal = Math.abs(grandTotal);
    const net = Math.max(payableTotal - paid, 0);
    const paymentStatus = getPaymentStatusFromTotals(
      payableTotal,
      paid,
    );

    setTotals({
      subTotal,
      totalDiscount,
      totalTax,
      shippingCost: shipping,
      otherCharges: other,
      grandTotal: displayGrandTotal,
      totalAmountPaid: paid,
      netAmount: net,
      paymentStatus,
    });

    setFormData((previous) => ({
      ...previous,
      payment_status: paymentStatus,
    }));
  }, [
    productsData,
    formData.shipping_cost,
    formData.other_charges,
    payments,
    isReturnMode,
    discounts,
    taxes,
  ]);

  useEffect(() => {
    const nonCreditRows = payments.filter((payment) => payment.method !== "credit");
    const hasNonCredit = nonCreditRows.length > 0;
    const hasNonCreditAmount = nonCreditRows.some((payment) => Number(payment.amount) > 0);
    const hasNonCreditWithoutAccount = nonCreditRows.some(
      (payment) => Number(payment.amount) > 0 && !payment.account_id,
    );

    if (hasNonCredit && !hasNonCreditAmount) {
      setPaymentInfo({
        message: "You selected an immediate payment method (Cash, Bank, Mobile Money) but entered zero amount. Enter an amount greater than zero to record payment now.",
        type: "warning",
      });
    } else if (hasNonCreditAmount && hasNonCreditWithoutAccount) {
      setPaymentInfo({
        message: "Please select a payment account for every immediate payment. Accounts Payable cannot be used as the payment account.",
        type: "error",
      });
    } else if (hasNonCreditAmount) {
      const amountPaidNow = nonCreditRows.reduce(
        (sum, payment) => sum + (Number(payment.amount) || 0),
        0,
      );
      setPaymentInfo({
        message: `Payment of ${formatCurrency(amountPaidNow)} will be recorded now. Any remaining balance will be added to Accounts Payable.`,
        type: "info",
      });
    } else if (payments.every((payment) => payment.method === "credit")) {
      setPaymentInfo({
        message: "Credit purchase selected. No payment will be recorded now. The supplier balance will be added to Accounts Payable and can be paid later from Supplier Payments.",
        type: "info",
      });
    } else {
      setPaymentInfo({
        message: "No immediate payment entered. The full purchase balance will be added to Accounts Payable.",
        type: "info",
      });
    }
  }, [payments, totals.grandTotal]);

  const handleFocus = (field) => setFocusedFields(prev => ({ ...prev, [field]: true }));
  const handleBlur = (field) => {
    const val = formData[field];
    setFocusedFields(prev => ({ ...prev, [field]: !!val }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'warehouse_id') {
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleProductSelect = async (index, productId) => {
    try {
      setProductsData(prev => {
        const updated = [...prev];
        updated[index].product_id = productId;
        return updated;
      });

      if (productId) {
        const stockInfo = productStock[productId];
        let avgCost = 0;

        if (stockInfo && stockInfo.avg_cost > 0) {
          avgCost = stockInfo.avg_cost;
        } else {
          const companyId = getCompanyId();
          const response = await axios.get(`${url}/api/products/${productId}`, {
            params: { company_id: companyId }
          });
          if (response.data.success) {
            const product = response.data.data;
            avgCost = product.purchase_cost || product.cost_price || product.purchase_price || 0;
          }
        }

        setProductsData(prev => {
          const updated = [...prev];
          updated[index].unit_cost = Number(avgCost || 0).toFixed(2);

          const calculated = calculateProductLine(updated[index], index, updated);
          updated[index].discount_percent =
            String(calculated.discountPercent);
          updated[index].discount_flat =
            String(calculated.discountFlat);
          updated[index].tax_amount =
            calculated.taxAmount.toFixed(2);
          updated[index].total_cost =
            calculated.total.toFixed(2);

          return updated;
        });
      }
    } catch (error) {
      toast.error("Failed to fetch product cost");
    }
  };

  const handleProductChange = (index, field, value) => {
    setProductsData((previous) => {
      const updated = previous.map((product) => ({ ...product }));
      updated[index][field] = value;

      const calculated = calculateProductLine(updated[index], index, updated);
      updated[index].discount_percent =
        String(calculated.discountPercent);
      updated[index].discount_flat =
        String(calculated.discountFlat);
      updated[index].tax_amount =
        calculated.taxAmount.toFixed(2);
      updated[index].total_cost =
        calculated.total.toFixed(2);

      return updated;
    });
  };

  const addProductRow = () => {
    setProductsData([...productsData, {
      product_id: "",
      quantity: "",
      unit_cost: "",
      discount_id: "",
      tax_id: "",
      discount_percent: "0",
      discount_flat: "0",
      manual_discount: "0",
      total_cost: "0",
      batch_number: "",
      expiry_date: "",
    }]);
  };

  const removeProductRow = (index) => {
    if (productsData.length > 1) {
      setProductsData(productsData.filter((_, i) => i !== index));
    }
  };

  const handlePaymentMethodChange = (index, field, value) => {
    const updated = payments.map((payment) => ({ ...payment }));

    if (field === "amount") {
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        updated[index].amount = value;
      }
    } else if (field === "method") {
      updated[index].method = value;
      updated[index].account_id = "";
      updated[index].account_name = "";
      if (value === "credit") updated[index].amount = "";
    } else if (field === "account_id") {
      updated[index].account_id = value;
      const selectedAccount = getAccountsForMethod(
        updated[index].method,
      ).find(
        (account) => String(account._id) === String(value),
      );
      updated[index].account_name =
        selectedAccount?.account_name || "";
    } else {
      updated[index][field] = value;
    }

    setPayments(updated);
  };

  const addPaymentRow = () => {
    setPayments([
      ...payments,
      {
        method: "cash",
        account_id: "",
        account_name: "",
        amount: "",
        reference: "",
      },
    ]);
  };

  const removePaymentRow = (index) => {
    if (payments.length > 1) {
      setPayments(payments.filter((_, i) => i !== index));
    }
  };

  const fetchReturnableTransactions = async () => {
    try {
      setLoadingReturnTransactions(true);
      const response = await axios.get(`${url}/api/purchase/dropdown`, {
        params: {
          company_id: getCompanyId(),
          exclude_returns: true,
        },
      });
      setReturnableTransactions(response.data?.data || []);
    } catch (error) {
      setReturnableTransactions([]);
      toast.error(error.response?.data?.message || "Failed to load returnable purchases");
    } finally {
      setLoadingReturnTransactions(false);
    }
  };

  const handleOriginalTransactionChange = async (transactionId) => {
    const selected = returnableTransactions.find((row) => String(row._id) === String(transactionId));
    setSelectedOriginalTransaction(selected || null);
    if (!selected) return;

    const returnType = transactionTypes.find((type) => {
      const name = String(type.transaction_type || "").toLowerCase();
      return name.includes("return") && type.sign === "-";
    });

    setFormData((previous) => ({
      ...previous,
      warehouse_id: selected.warehouse?._id || "",
      supplier_id: selected.supplier?._id || "",
      transaction_type_id: returnType?._id || previous.transaction_type_id,
      invoice_no: `PR-${Date.now()}`,
      return_reason: "",
    }));

    const originalDiscountId = getRecordId(
      selected.discount_id || selected.discount,
    );
    const originalTaxId = getRecordId(selected.tax_id || selected.tax);

    setPurchaseAdjustments({
      discount_id: originalDiscountId ? String(originalDiscountId) : "",
      tax_id: originalTaxId ? String(originalTaxId) : "",
      manual_discount: "",
    });

    setProductsData([{
      product_id: getRecordId(selected.product) || "",
      quantity: "",
      unit_cost: Number(selected.unit_cost || 0).toFixed(2),
      discount_id: originalDiscountId ? String(originalDiscountId) : "",
      tax_id: originalTaxId ? String(originalTaxId) : "",
      discount_percent: String(selected.discount_percent || 0),
      discount_flat: String(selected.discount_flat || 0),
      manual_discount: "0",
      tax_amount: "0",
      total_cost: "0",
      batch_number: selected.batch_number || "",
      expiry_date: selected.expiry_date
        ? String(selected.expiry_date).split("T")[0]
        : "",
    }]);

    const warehouseId = selected.warehouse?._id;
    if (warehouseId) await fetchProductsForWarehouse(warehouseId);
  };

  const handleReturnModeToggle = async () => {
    const nextReturnMode = !isReturnMode;
    const nextTransactionType = getModeTransactionType(
      transactionTypes,
      nextReturnMode,
    );

    setIsReturnMode(nextReturnMode);
    setFormData((previous) => ({
      ...previous,
      transaction_type_id: nextTransactionType
        ? String(getRecordId(nextTransactionType))
        : "",
      return_reason: "",
    }));

    if (nextReturnMode) await fetchReturnableTransactions();

    setProductsData([{
      product_id: "",
      quantity: "",
      unit_cost: "",
      discount_id: "",
      tax_id: "",
      discount_percent: "0",
      discount_flat: "0",
      manual_discount: "0",
      total_cost: "0",
      batch_number: "",
      expiry_date: "",
    }]);

    setPayments([
      {
        method: "credit",
        account_id: "",
        account_name: "",
        amount: "",
        reference: "",
      },
    ]);
    setPurchaseAdjustments({
      discount_id: "",
      tax_id: "",
      manual_discount: "",
    });
    setPaymentHistory([]);
    setShowPaymentHistory(false);
    setSelectedOriginalTransaction(null);
  };

  const handleCancel = () => {
    navigate("/purchase/invoices");
  };

  const validateForm = () => {
    if (!formData.warehouse_id) {
      toast.error("Warehouse is required");
      return false;
    }
    if (!formData.supplier_id) {
      toast.error("Supplier is required");
      return false;
    }

    const validProducts = productsData.filter(
      (product) => product.product_id && product.quantity && product.unit_cost,
    );
    if (validProducts.length === 0) {
      toast.error("At least one valid product is required");
      return false;
    }

    for (const payment of payments) {
      const amount = Number(payment.amount || 0);
      if (payment.method === "credit") {
        if (amount > 0) {
          toast.error("Credit (Pay Later) cannot have a payment amount. Set the Credit amount to 0.");
          return false;
        }
        continue;
      }

      if (amount <= 0) {
        toast.error(`${String(payment.method || "Payment").replaceAll("_", " ")} payment amount must be greater than zero.`);
        return false;
      }
      if (!payment.account_id) {
        toast.error(`Please select a payment account for ${String(payment.method || "payment").replaceAll("_", " ")}.`);
        return false;
      }
    }

    const paidTotal = payments.reduce(
      (sum, payment) =>
        sum + (payment.method === "credit" ? 0 : Number(payment.amount || 0)),
      0,
    );

    if (paidTotal > Math.abs(totals.grandTotal) + 0.005) {
      const excess = paidTotal - Math.abs(totals.grandTotal);
      const label = isReturnMode ? "Refund" : "Payment";
      toast.error(
        `${label} exceeds the total by ${formatCurrency(excess)}. ` +
        `Total: ${formatCurrency(Math.abs(totals.grandTotal))} • ` +
        `Entered: ${formatCurrency(paidTotal)}. ` +
        `Reduce the ${label.toLowerCase()} by ${formatCurrency(excess)} to continue.`
      );
      return false;
    }

    if (isReturnMode && !selectedOriginalTransaction?._id) {
      toast.error("Select the original purchase transaction");
      return false;
    }
    if (isReturnMode && !formData.return_reason) {
      toast.error("Return reason is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const companyId = getCompanyId();

      const validPayments = payments.filter(
        (payment) =>
          payment.method !== "credit" &&
          Number(payment.amount || 0) > 0 &&
          payment.account_id,
      );
      const validProducts = productsData.filter(p => p.product_id && p.quantity && p.unit_cost);

      if (isReturnMode) {
        const returnLine = validProducts[0];
        const returnPayload = {
          original_transaction_id: selectedOriginalTransaction._id,
          return_quantity: Number(returnLine.quantity),
          return_reason: formData.return_reason.trim(),
          notes: formData.notes || formData.return_reason.trim(),
          payment_status: totals.paymentStatus,
          total_amount_paid: Number(totals.totalAmountPaid || 0),
          payments: validPayments.map((payment) => ({
            method: payment.method,
            account_id: payment.account_id,
            account_name: payment.account_name || "",
            amount: Number(payment.amount || 0),
            reference: payment.reference || "",
          })),
        };

        const returnResponse = await axios.post(`${url}/api/purchase/return`, returnPayload);

        if (returnResponse.data?.success) {
          toast.success(`Purchase return created successfully: ${returnResponse.data.data?.return_transaction?.reference_id || ""}`);
          setTimeout(() => navigate("/purchase/invoices"), 1200);
        }
        return;
      }

      const payload = {
        invoice: {
          company_id: companyId,
          supplier_id: formData.supplier_id,
          warehouse_id: formData.warehouse_id,
          transaction_type_id: formData.transaction_type_id,
          invoice_no: formData.invoice_no || (isReturnMode ? `PR-${Date.now()}` : `PU-${Date.now()}`),
          invoice_date: formData.invoice_date,
          due_date: formData.due_date,
          sub_total: Math.abs(totals.subTotal),
          total_discount: totals.totalDiscount,
          manual_discount: Number(purchaseAdjustments.manual_discount || 0),
          discount_id: purchaseAdjustments.discount_id || null,
          total_tax: totals.totalTax,
          tax_id: purchaseAdjustments.tax_id || null,
          shipping_cost: parseFloat(formData.shipping_cost) || 0,
          other_charges: parseFloat(formData.other_charges) || 0,
          grand_total: Math.abs(totals.grandTotal),
          payment_method: validPayments.map(p => p.method),
          payment_status: totals.paymentStatus,
          status: totals.paymentStatus === "paid" ? "completed" : "process",
          notes: formData.notes,
          is_return: isReturnMode,
          return_reason: formData.return_reason,
          total_amount_paid: totals.totalAmountPaid,
          net_amount: Math.abs(totals.netAmount),
        },
        transactions: validProducts.map((product, index) => {
          const calculated = calculateProductLine(product, index, validProducts);
          const discount = discounts.find(
            (row) => String(row._id) === String(product.discount_id),
          );
          const tax = taxes.find(
            (row) => String(row._id) === String(product.tax_id),
          );

          return {
            product_id: product.product_id,
            warehouse_id: formData.warehouse_id,
            supplier_id: formData.supplier_id,
            transaction_type_id: formData.transaction_type_id,
            quantity: Number(product.quantity),
            unit_cost: Number(product.unit_cost),
            discount_id: product.discount_id || null,
            discount_name: discount?.name || "",
            discount_code: discount?.code || "",
            discount_value: Number(getDiscountValue(discount) || 0),
            discount_percent: calculated.discountPercent,
            discount_flat: calculated.discountFlat,
            discount_amount: calculated.discountAmount,
            manual_discount: Number(product.manual_discount || 0),
            tax_id: product.tax_id || null,
            tax_name: tax?.name || "",
            tax_code: tax?.code || "",
            tax_value: Number(getTaxValue(tax) || 0),
            tax_amount: calculated.taxAmount,
            batch_number: product.batch_number || "",
            expiry_date: product.expiry_date || null,
            is_return: false,
          };
        }),
        payments: validPayments.map((payment) => ({
          method: payment.method,
          account_id: payment.account_id,
          amount: Number(payment.amount),
          reference: payment.reference || "",
        }))
      };

      const res = await axios.post(`${url}/api/purchase`, payload);

      if (res.data.success) {
        toast.success(`Transaction created successfully! Reference: ${res.data.data.referenceId}`);
        setTimeout(() => navigate("/purchase/invoices"), 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash': return <CreditCardIcon size={14} />;
      case 'bank': return <Banknote size={14} />;
      case 'mobile_money': return <Smartphone size={14} />;
      case 'credit': return <CircleDollarSign size={14} />;
      default: return <DollarSign size={14} />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: 'Cash',
      bank: 'Bank',
      mobile_money: 'Mobile Money',
      credit: 'Credit'
    };
    return labels[method] || method;
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      paid: { bg: '#f6ffed', color: '#52c41a', label: 'Paid' },
      partial: { bg: '#fff7e6', color: '#fa8c16', label: 'Partial' },
      notpaid: { bg: '#fff1f0', color: '#f5222d', label: 'Not Paid' },
      pending: { bg: '#e6f7ff', color: '#1890ff', label: 'Pending' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: config.bg,
        color: config.color
      }}>
        {config.label}
      </span>
    );
  };

  // ✅ Overpayment / Over-refund detection — works in BOTH Purchase and Return mode
  const overpaidAmount = Math.max(
    0,
    totals.totalAmountPaid - Math.abs(totals.grandTotal)
  );
  const hasOverpayment = overpaidAmount > 0.005;

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="purchase-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading purchase form data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-wrapper">
        <div className="purchase-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={handleCancel} className="cancel-btn">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="purchase-form-container">
        <button className="close-btn" onClick={handleCancel} type="button">
          <X size={20} />
        </button>

        <div className="form-header">
          <Package size={24} />
          <h2>{isReturnMode ? 'Create Purchase Return' : 'Create Purchase Transaction'}</h2>
        </div>

        <div style={{
          marginBottom: 20,
          padding: '10px 15px',
          background: totals.paymentStatus === 'paid' ? '#f6ffed' :
            totals.paymentStatus === 'partial' ? '#fff7e6' :
              totals.paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
          border: `1px solid ${totals.paymentStatus === 'paid' ? '#b7eb8f' : totals.paymentStatus === 'partial' ? '#ffd591' : totals.paymentStatus === 'notpaid' ? '#ffa39e' : '#91d5ff'}`,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarSign size={16} color={totals.paymentStatus === 'paid' ? '#52c41a' : totals.paymentStatus === 'partial' ? '#fa8c16' : totals.paymentStatus === 'notpaid' ? '#f5222d' : '#1890ff'} />
            <span style={{ fontWeight: 500 }}>Payment Status:</span>
          </div>
          {getPaymentStatusBadge(totals.paymentStatus)}
        </div>

        <div style={{
          marginBottom: 20,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          padding: '10px',
          background: isReturnMode ? '#fff2f0' : '#e6f7ff',
          border: isReturnMode ? '1px solid #ffccc7' : '1px solid #bae7ff',
          borderRadius: 4
        }}>
          <button
            type="button"
            onClick={handleReturnModeToggle}
            style={{
              padding: '8px 16px',
              background: isReturnMode ? '#ff4d4f' : '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <ArrowLeftRight size={14} />
            {isReturnMode ? 'Switch to Purchase' : 'Switch to Return'}
          </button>
          <span style={{ color: isReturnMode ? '#ff4d4f' : '#1890ff', fontWeight: 500 }}>
            {isReturnMode ? (
              <>RETURN MODE - Inventory will be decreased <ArrowDown size={14} style={{ marginLeft: 4 }} /></>
            ) : (
              <>PURCHASE MODE - Inventory will be increased <ArrowUp size={14} style={{ marginLeft: 4 }} /></>
            )}
          </span>
        </div>

        {formData.purchase_invoice_id && paymentHistory.length > 0 && (
          <div style={{ marginBottom: 20, padding: 15, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <History size={18} color="#722ed1" />
              <h3 style={{ margin: 0, fontSize: 16, color: '#722ed1' }}>Payment History</h3>
              <span style={{ marginLeft: 'auto', background: '#722ed1', color: 'white', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                Total: {formatCurrency(totalPayments)}
              </span>
              <button
                type="button"
                onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                style={{ padding: '4px 8px', background: '#722ed1', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
              >
                {showPaymentHistory ? 'Hide' : 'Show'}
              </button>
            </div>

            {showPaymentHistory && (
              <div style={{ overflowX: 'auto' }}>
                {loadingPayments ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <Loader2 className="animate-spin" size={24} />
                    <p>Loading payment history...</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f0f0f0' }}>
                        <th style={{ padding: 8, textAlign: 'left' }}>Payment No</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Date</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Payment Methods</th>
                        <th style={{ padding: 8, textAlign: 'right' }}>Amount</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Payment Status</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Document Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((payment, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: 8 }}><strong>{payment.payment_no}</strong></td>
                          <td style={{ padding: 8 }}>{formatDate(payment.payment_date)}</td>
                          <td style={{ padding: 8 }}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {payment.payment_methods?.map((pm, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', background: '#f5f5f5', borderRadius: 4, fontSize: 11 }}>
                                  {getPaymentMethodIcon(pm.method)}
                                  {getPaymentMethodLabel(pm.method)}: {formatCurrency(pm.amount)}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td style={{ padding: 8, textAlign: 'right', fontWeight: 600, color: '#52c41a' }}>{formatCurrency(payment.total_paying_now)}</td>
                          <td style={{ padding: 8 }}>{getPaymentStatusBadge(payment.payment_status)}</td>
                          <td style={{ padding: 8 }}>
                            <span style={{ padding: '2px 8px', background: payment.status === 'approved' ? '#f6ffed' : payment.status === 'process' ? '#e6f7ff' : payment.status === 'submitted' ? '#fff7e6' : '#fff1f0', color: payment.status === 'approved' ? '#52c41a' : payment.status === 'process' ? '#1890ff' : payment.status === 'submitted' ? '#fa8c16' : '#f5222d', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#fafafa', borderTop: '2px solid #ddd' }}>
                        <td colSpan="3" style={{ padding: 8, fontWeight: 600 }}>Total Payments</td>
                        <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: '#1890ff', fontSize: 14 }}>{formatCurrency(totalPayments)}</td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Warehouse *</label>
              <Select
                value={
                  warehouses
                    .filter(
                      (warehouse) =>
                        String(warehouse._id) === String(formData.warehouse_id)
                    )
                    .map((warehouse) => ({
                      value: warehouse._id,
                      label: `${warehouse.warehouse_name} (${warehouse.warehouse_code})`,
                    }))[0] || null
                }

                options={warehouses.map((warehouse) => ({
                  value: warehouse._id,
                  label: `${warehouse.warehouse_name} (${warehouse.warehouse_code})`,
                }))}

                onChange={handleWarehouseSelect}

                placeholder="Search Warehouse..."

                isClearable

                isDisabled={isReturnMode}

                noOptionsMessage={() => "No warehouse found"}

                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }),

                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),

                  menuList: (base) => ({
                    ...base,
                    maxHeight: 250,
                  }),
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Supplier *</label>
              <Select
                value={
                  suppliers
                    .filter(
                      (supplier) =>
                        String(supplier._id) === String(formData.supplier_id)
                    )
                    .map((supplier) => ({
                      value: supplier._id,
                      label: `${supplier.supplier_name} (${supplier.supplier_id}${supplier.phone ? ` - ${supplier.phone}` : ''})`,
                    }))[0] || null
                }

                options={suppliers.map((supplier) => ({
                  value: supplier._id,
                  label: `${supplier.supplier_name} (${supplier.supplier_id}${supplier.phone ? ` - ${supplier.phone}` : ''})`,
                }))}

                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    supplier_id: selected ? selected.value : "",
                  }))
                }

                placeholder="Search Supplier..."

                isClearable

                isDisabled={isReturnMode}

                noOptionsMessage={() => "No supplier found"}

                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }),

                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),

                  menuList: (base) => ({
                    ...base,
                    maxHeight: 250,
                  }),
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Transaction Type</label>
              <select
                name="transaction_type_id"
                value={formData.transaction_type_id}
                disabled
                aria-readonly="true"
                style={{
                  width: '100%',
                  padding: 8,
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  background: '#f5f5f5',
                  cursor: 'not-allowed',
                }}
                required
              >
                <option value="">
                  {transactionTypes.length === 0
                    ? 'Transaction type unavailable'
                    : 'Selecting transaction type...'}
                </option>
                {transactionTypes.map((type) => (
                  <option key={getRecordId(type)} value={getRecordId(type)}>
                    {type.transaction_type} ({type.sign})
                  </option>
                ))}
              </select>
              <small style={{ color: '#666' }}>
                Automatically set to {isReturnMode ? 'purchase_return (-)' : 'purchase (+)'} by the selected mode.
              </small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Invoice No (Optional)</label>
              <input type="text" name="invoice_no" value={formData.invoice_no} onChange={handleChange} placeholder="Auto-generated if empty" style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Invoice Date</label>
              <input type="date" name="invoice_date" value={formData.invoice_date} onChange={handleChange} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Due Date</label>
              <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>

            {isReturnMode && (
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Original Purchase Transaction *</label>
                <select
                  value={selectedOriginalTransaction?._id || ""}
                  onChange={(event) => handleOriginalTransactionChange(event.target.value)}
                  disabled={loadingReturnTransactions}
                  required
                  style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', marginBottom: 12 }}
                >
                  <option value="">{loadingReturnTransactions ? "Loading purchases..." : "Select original purchase"}</option>
                  {returnableTransactions.map((transaction) => (
                    <option key={transaction._id} value={transaction._id}>
                      {transaction.reference_id} - {transaction.product?.name} - Purchased: {Math.abs(Number(transaction.quantity || 0))}
                    </option>
                  ))}
                </select>
                {selectedOriginalTransaction && (
                  <div style={{ padding: 10, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
                    Supplier: <strong>{selectedOriginalTransaction.supplier?.name}</strong> | Warehouse: <strong>{selectedOriginalTransaction.warehouse?.name}</strong> | Maximum return: <strong>{Math.abs(Number(selectedOriginalTransaction.quantity || 0))}</strong> | Original discount: <strong>{formatCurrency(Math.abs(Number(selectedOriginalTransaction.discount_amount || 0)))}</strong> | Original tax: <strong>{formatCurrency(Math.abs(Number(selectedOriginalTransaction.tax_amount || 0)))}</strong> | Original shipping: <strong>{formatCurrency(Math.abs(Number(selectedOriginalTransaction.shipping_cost || 0)))}</strong> | Original other charges: <strong>{formatCurrency(Math.abs(Number(selectedOriginalTransaction.other_charges || 0)))}</strong>
                  </div>
                )}
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Return Reason *</label>
                <input type="text" name="return_reason" value={formData.return_reason} onChange={handleChange} placeholder="Reason for return..." style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} required={isReturnMode} />
              </div>
            )}
          </div>

          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 15 }}>
            <Package size={18} /> Products
            {formData.warehouse_id && (
              <span style={{ fontSize: 13, color: '#666', fontWeight: 'normal' }}>
                (Showing stock for selected warehouse)
              </span>
            )}
          </h3>

          {productsData.map((product, idx) => (
            <div key={idx} style={{ border: '1px solid #ddd', padding: 15, marginBottom: 15, borderRadius: 4, position: 'relative', background: '#fafafa' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '4fr 1fr 1.4fr 1.2fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Product</label>
                  <Select
                    value={
                      filteredProducts
                        .filter((p) => String(p._id) === String(product.product_id))
                        .map((p) => ({
                          value: p._id,
                          label:
                            `${p.product_name} (${p.product_code})` +
                            (p.available_stock !== undefined
                              ? ` | Stock: ${p.available_stock}`
                              : "") +
                            (p.avg_cost !== undefined && p.avg_cost !== null
                              ? ` | Avg Cost: ${formatCurrency(p.avg_cost)}`
                              : ""),
                        }))[0] || null
                    }

                    options={
                      filteredProducts
                        .filter((p) => {
                          const alreadySelected = productsData.some(
                            (row, rowIndex) =>
                              rowIndex !== idx &&
                              String(row.product_id) === String(p._id)
                          );
                          return !alreadySelected;
                        })
                        .map((p) => ({
                          value: p._id,
                          label:
                            `${p.product_name} (${p.product_code})` +
                            (p.available_stock !== undefined
                              ? ` | Stock: ${p.available_stock}`
                              : "") +
                            (p.avg_cost !== undefined && p.avg_cost !== null
                              ? ` | Avg Cost: ${formatCurrency(p.avg_cost)}`
                              : ""),
                        }))
                    }

                    onChange={(selected) =>
                      handleProductSelect(
                        idx,
                        selected ? selected.value : ""
                      )
                    }

                    placeholder="Search Product..."

                    isDisabled={isReturnMode}

                    isClearable

                    noOptionsMessage={() =>
                      "No available products"
                    }

                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }),

                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),

                      menuList: (base) => ({
                        ...base,
                        maxHeight: 250,
                      }),
                    }}
                  />
                  {product.product_id && productStock[product.product_id] && (
                    <div style={{ fontSize: 11, marginTop: 4, color: '#52c41a' }}>
                      <Layers size={12} style={{ display: 'inline', marginRight: 4 }} />
                      Available: {productStock[product.product_id].available_stock} units
                      {!isReturnMode && productStock[product.product_id].avg_cost > 0 && (
                        <> | Avg Cost: {formatCurrency(productStock[product.product_id].avg_cost)}</>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Quantity</label>
                  <input type="number" placeholder="Qty" value={product.quantity}
                    max={isReturnMode ? Math.abs(Number(selectedOriginalTransaction?.quantity || 0)) : undefined}
                    onChange={(e) => handleProductChange(idx, 'quantity', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} required min="1" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Unit Cost</label>
                  <input type="number" placeholder="Cost" value={product.unit_cost} disabled={isReturnMode}
                    onChange={(e) => handleProductChange(idx, 'unit_cost', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} required min="0" step="0.01" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Line Total</label>
                  <input type="text" value={formatCurrency(parseFloat(product.total_cost) || 0)} readOnly
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div>
                  <input
                    type="text"
                    placeholder="Batch Number (optional)"
                    value={product.batch_number}
                    onChange={(e) => handleProductChange(idx, 'batch_number', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Expire Date</label>
                  <input
                    type="date"
                    placeholder="Expiry Date"
                    value={product.expiry_date}
                    onChange={(e) => handleProductChange(idx, 'expiry_date', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </div>
              </div>

              {idx > 0 && (
                <button
                  type="button"
                  onClick={() => removeProductRow(idx)}
                  style={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    background: '#fff1f0',
                    border: '1px solid #ffccc7',
                    borderRadius: 4,
                    color: '#ff4d4f',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addProductRow}
            style={{
              marginBottom: 20,
              padding: '8px 16px',
              background: 'transparent',
              border: '2px dashed #1890ff',
              borderRadius: 4,
              color: '#1890ff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
            disabled={!formData.warehouse_id || isReturnMode}
          >
            <Plus size={16} /> Add Product
          </button>

          {!formData.warehouse_id && (
            <div style={{ fontSize: 13, color: '#fa8c16', marginBottom: 20 }}>
              Please select a warehouse first to see available products
            </div>
          )}

          {isReturnMode && selectedOriginalTransaction && (
            <>
              <h3 style={{ marginBottom: 15 }}>Original Purchase Discount and Tax</h3>
              <div style={{ padding: 18, border: '1px solid #ffd591', borderRadius: 8, marginBottom: 20, background: '#fff7e6' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Original Discount</label>
                    <input type="text" readOnly
                      value={formatCurrency(Math.abs(Number(selectedOriginalTransaction.discount_amount || 0)))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                    <small style={{ color: '#666' }}>
                      {Number(selectedOriginalTransaction.discount_percent || 0) > 0
                        ? `${Number(selectedOriginalTransaction.discount_percent).toFixed(2)}% from the original purchase`
                        : 'Saved original discount amount'}
                    </small>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Original Tax</label>
                    <input type="text" readOnly
                      value={formatCurrency(Math.abs(Number(selectedOriginalTransaction.tax_amount || 0)))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                    <small style={{ color: '#666' }}>Reversed proportionally based on return quantity.</small>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Original Shipping</label>
                    <input type="text" readOnly
                      value={formatCurrency(Math.abs(Number(selectedOriginalTransaction.shipping_cost || 0)))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                    <small style={{ color: '#666' }}>Reversed proportionally based on return quantity.</small>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Original Other Charges</label>
                    <input type="text" readOnly
                      value={formatCurrency(Math.abs(Number(selectedOriginalTransaction.other_charges || 0)))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                    <small style={{ color: '#666' }}>Reversed proportionally based on return quantity.</small>
                  </div>
                </div>
              </div>
            </>
          )}

          {!isReturnMode && (
            <>
              <h3 style={{ marginBottom: 15 }}>Purchase Discount and Tax</h3>
              <div style={{ padding: 18, border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 20, background: '#fafafa' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Apply Discount to All Items</label>
                    <select value={purchaseAdjustments.discount_id}
                      onChange={(e) => setPurchaseAdjustments((prev) => ({ ...prev, discount_id: e.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }}>
                      <option value="">No Discount</option>
                      {discounts.map((discount) => (
                        <option key={discount._id} value={discount._id}>
                          {discount.name} ({discount.code}) - {discount.discount_type === 'percentage' ? `${discount.value}%` : `${formatCurrency(discount.value)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Apply Tax to All Items</label>
                    <select value={purchaseAdjustments.tax_id}
                      onChange={(e) => setPurchaseAdjustments((prev) => ({ ...prev, tax_id: e.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }}>
                      <option value="">No Tax</option>
                      {taxes.map((tax) => (
                        <option key={tax._id} value={tax._id}>
                          {tax.name} ({tax.code}) - {tax.tax_type === 'percentage' ? `${tax.value}%` : `${formatCurrency(tax.value)}`}
                          {tax.price_inclusive ? ' · Inclusive' : ' · Exclusive'}{tax.recoverable ? ' · Recoverable' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Manual Discount Amount</label>
                    <input type="number" min="0" step="0.01" placeholder="Example: 5.00"
                      value={purchaseAdjustments.manual_discount}
                      onChange={(e) => setPurchaseAdjustments((prev) => ({ ...prev, manual_discount: e.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }} />
                    <small style={{ color: '#666' }}>This amount is added to the selected discount and allocated across items.</small>
                  </div>
                </div>
              </div>
            </>
          )}

          <h3 style={{ marginBottom: 15 }}>Additional Charges</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Shipping Cost</label>
              <input
                type="number"
                name="shipping_cost"
                value={formData.shipping_cost}
                onChange={handleChange}
                disabled={isReturnMode}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: isReturnMode ? '#f5f5f5' : 'white' }}
                min="0"
                step="0.01"
              />
              {isReturnMode && totals.shippingCost > 0 && (
                <small style={{ color: '#666' }}>
                  Reversed from original: {formatCurrency(totals.shippingCost)}
                </small>
              )}
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Other Charges</label>
              <input
                type="number"
                name="other_charges"
                value={formData.other_charges}
                onChange={handleChange}
                disabled={isReturnMode}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: isReturnMode ? '#f5f5f5' : 'white' }}
                min="0"
                step="0.01"
              />
              {isReturnMode && totals.otherCharges > 0 && (
                <small style={{ color: '#666' }}>
                  Reversed from original: {formatCurrency(totals.otherCharges)}
                </small>
              )}
            </div>
          </div>

          <h3 style={{ marginBottom: 15 }}>Payments</h3>
          {paymentMethodsData.methods.length === 0 ? (
            <div style={{ padding: 20, background: '#fafafa', borderRadius: 8, marginBottom: 20, textAlign: 'center', border: '1px dashed #ddd' }}>
              <AlertCircle size={32} color="#fa8c16" />
              <p style={{ marginTop: 10, color: '#666' }}>
                No approved Cash, Bank, Mobile Money, or Other posting accounts are configured.
              </p>
            </div>
          ) : (
            <>
              {payments.map((payment, idx) => (
                <div key={idx} style={{ marginBottom: 15, padding: 15, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0', position: 'relative' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 10, alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Payment Method</label>
                      <select value={payment.method || ""} onChange={(e) => handlePaymentMethodChange(idx, 'method', e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}>
                        <option value="">Select Payment Method</option>
                        {paymentMethodsData.methods.map((method) => (
                          <option key={method.key} value={method.key}>{method.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Account</label>
                      <select value={payment.account_id || ""} onChange={(e) => handlePaymentMethodChange(idx, 'account_id', e.target.value)} disabled={payment.method === 'credit'} required={payment.method !== 'credit' && Number(payment.amount) > 0} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: payment.method === 'credit' ? '#f5f5f5' : 'white' }}>
                        <option value="">{payment.method === 'credit' ? 'Accounts Payable (automatic)' : 'Select Account'}</option>
                        {getAccountsForMethod(payment.method).map((account) => (
                          <option key={account._id} value={account._id}>
                            {account.account_code} - {account.account_name} (Balance: {formatCurrency(account.balance || 0)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Amount</label>
                      <input type="number" placeholder={payment.method === 'credit' ? 'Remaining balance' : '0.00'} value={payment.amount} onChange={(e) => handlePaymentMethodChange(idx, 'amount', e.target.value)} disabled={payment.method === 'credit'} min="0" step="0.01" style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: payment.method === 'credit' ? '#f5f5f5' : 'white' }} />
                    </div>
                    {payments.length > 1 && (
                      <button type="button" onClick={() => removePaymentRow(idx)} style={{ marginTop: 20, padding: '8px 16px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 4, color: '#ff4d4f', cursor: 'pointer' }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <input type="text" placeholder="Reference / Cheque No (optional)" value={payment.reference || ""} onChange={(e) => handlePaymentMethodChange(idx, 'reference', e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addPaymentRow}
                disabled={isReturnMode} style={{ marginBottom: 20, padding: '8px 16px', background: 'transparent', border: '2px dashed #52c41a', borderRadius: 4, color: '#52c41a', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Plus size={16} /> Add Another Payment Method
              </button>
            </>
          )}

          {paymentInfo.message && (
            <div style={{
              marginBottom: 20,
              padding: '12px 16px',
              borderRadius: 4,
              background: paymentInfo.type === 'error' ? '#fff1f0' :
                paymentInfo.type === 'warning' ? '#fffbe6' : '#e6f7ff',
              border: `1px solid ${paymentInfo.type === 'error' ? '#ffa39e' :
                paymentInfo.type === 'warning' ? '#ffe58f' : '#91d5ff'}`,
              color: paymentInfo.type === 'error' ? '#cf1322' :
                paymentInfo.type === 'warning' ? '#d48806' : '#0050b3',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10
            }}>
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>{paymentInfo.message}</span>
            </div>
          )}

          <div style={{
            background: '#f5f5f5',
            padding: 20,
            borderRadius: 4,
            marginBottom: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Subtotal:</span>
              <strong>{formatCurrency(totals.subTotal)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Total Discount:</span>
              <strong style={{ color: '#e53935' }}>-{formatCurrency(totals.totalDiscount)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Tax:</span>
              <strong>+{formatCurrency(totals.totalTax)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Shipping:</span>
              <strong>+{formatCurrency(totals.shippingCost)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Other:</span>
              <strong>+{formatCurrency(totals.otherCharges)}</strong>
            </div>

            {totalPayments > 0 && (
              <div style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px dashed #1890ff',
                background: '#e6f7ff',
                padding: '10px',
                borderRadius: 4
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontWeight: 600 }}>Previous Payments:</span>
                  <strong style={{ color: '#1890ff' }}>{formatCurrency(totalPayments)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>Total Paid (Including Previous):</span>
                  <strong style={{ color: '#52c41a', fontSize: 16 }}>
                    {formatCurrency(totalPayments + totals.totalAmountPaid)}
                  </strong>
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 'bold',
              marginTop: 10,
              paddingTop: 10,
              borderTop: '2px solid #ddd'
            }}>
              <span>Grand Total:</span>
              <strong style={{ color: isReturnMode ? '#ff4d4f' : '#1890ff', fontSize: 18 }}>
                {formatCurrency(totals.grandTotal)}
              </strong>
            </div>

            <div style={{
              marginTop: 15,
              paddingTop: 15,
              borderTop: '1px dashed #ddd'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Amount Paid Now:</span>
                <strong style={{ color: '#52c41a' }}>{formatCurrency(totals.totalAmountPaid)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Supplier Balance (After This Payment):</span>
                <strong style={{
                  color: totals.netAmount > 0 ? '#ff4d4f' : '#52c41a',
                  fontWeight: 'bold'
                }}>
                  {formatCurrency(totals.netAmount)}
                </strong>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 10,
                padding: '8px 12px',
                background: totals.paymentStatus === 'paid' ? '#f6ffed' :
                  totals.paymentStatus === 'partial' ? '#fff7e6' :
                    totals.paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
                border: `1px solid ${totals.paymentStatus === 'paid' ? '#b7eb8f' :
                  totals.paymentStatus === 'partial' ? '#ffd591' :
                    totals.paymentStatus === 'notpaid' ? '#ffa39e' : '#91d5ff'
                  }`,
                borderRadius: 4
              }}>
                <span style={{ fontWeight: 600 }}>Payment Status:</span>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  background: totals.paymentStatus === 'paid' ? '#f6ffed' :
                    totals.paymentStatus === 'partial' ? '#fff7e6' :
                      totals.paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
                  color: totals.paymentStatus === 'paid' ? '#52c41a' :
                    totals.paymentStatus === 'partial' ? '#fa8c16' :
                      totals.paymentStatus === 'notpaid' ? '#f5222d' : '#1890ff'
                }}>
                  {totals.paymentStatus === 'paid' ? 'Paid' :
                    totals.paymentStatus === 'partial' ? 'Partial' :
                      totals.paymentStatus === 'notpaid' ? 'Not Paid' : 'Pending'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <textarea
              name="notes"
              placeholder="Additional notes..."
              value={formData.notes}
              onChange={handleChange}
              style={{ width: '100%', padding: 8, minHeight: 80, borderRadius: 4, border: '1px solid #ddd' }}
            />
          </div>

          {/* ✅ Overpayment / Over-refund warning — works in BOTH Purchase and Return mode */}
          {hasOverpayment && (
            <div
              style={{
                marginBottom: 15,
                padding: '12px 16px',
                background: '#fff1f0',
                border: '1px solid #ffa39e',
                borderLeft: '4px solid #f5222d',
                borderRadius: 4,
                color: '#cf1322',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                <strong style={{ display: 'block', marginBottom: 4 }}>
                  {isReturnMode
                    ? "Refund amount cannot exceed the return total."
                    : "Payment amount cannot exceed the purchase total."}
                </strong>
                <div style={{ marginBottom: 4 }}>
                  Total: <strong>{formatCurrency(Math.abs(totals.grandTotal))}</strong>
                  {" • "}
                  Entered: <strong>{formatCurrency(totals.totalAmountPaid)}</strong>
                  {" • "}
                  Excess: <strong style={{ color: '#f5222d' }}>{formatCurrency(overpaidAmount)}</strong>
                </div>
                <div>
                  Reduce the {isReturnMode ? "refund" : "payment"} by{" "}
                  <strong>{formatCurrency(overpaidAmount)}</strong> to continue.
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <X size={16} /> Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || hasOverpayment}
              title={hasOverpayment
                ? `${isReturnMode ? "Refund" : "Payment"} exceeds total by ${formatCurrency(overpaidAmount)}`
                : ""}
              style={{
                padding: '10px 20px',
                background: hasOverpayment
                  ? '#d9d9d9'
                  : (isReturnMode ? '#ff4d4f' : '#1890ff'),
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: submitting
                  ? 'wait'
                  : hasOverpayment
                    ? 'not-allowed'
                    : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Creating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Create {isReturnMode ? 'Return' : 'Purchase'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseTransaction;
